import styled from "@emotion/styled/macro";

/* ═══════════════════════════════════════════════════════════════
   GynaeConsultationStyles — Styled components for
   Gynaecological Consultation Module (Task 18).
   Theme: Teal/Dark Cyan (#00796b) — gynaecological care
   ═══════════════════════════════════════════════════════════════ */

const PRIMARY = "#005a8b";
const PRIMARY_LIGHT = "#e8f0fe";
const PRIMARY_BG = "#f8fafd";
const SECONDARY = "#2c3e50";
const SUCCESS = "#1b5e20";
const WARNING = "#e65100";
const DANGER = "#b71c1c";
const INFO = "#0d47a1";
const PURPLE = "#4a148c";

/* ─── Page Wrapper ─── */
export const GYPageWrapper = styled("div")({
    padding: "8px 12px 16px",
    background: "#ffffff",
    height: "calc(100vh - 120px)",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
});

/* ─── Header ─── */
export const GYHeader = styled("div")({
    background: "#f0f4f8",
    borderRadius: "4px",
    padding: "12px 16px",
    marginBottom: "12px",
    color: "#333",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    border: "1px solid #dce4ec",
    flexShrink: 0,
});

export const GYHeaderLeft = styled("div")({ display: "flex", flexDirection: "column", gap: "2px" });
export const GYHeaderTitle = styled("h2")({ margin: 0, fontSize: "1.1rem", fontWeight: 600, color: "#333" });
export const GYHeaderSub = styled("p")({ margin: 0, fontSize: "0.8rem", color: "#666" });

export const GYHeaderStats = styled("div")({ display: "flex", gap: "10px", flexWrap: "wrap" });

export const GYStatChip = styled("div")(function (props) {
    let bg = "rgba(255,255,255,0.15)";
    let border = "rgba(255,255,255,0.3)";
    if (props.variant === "success") { bg = "rgba(46,125,50,0.3)"; border = "rgba(46,125,50,0.5)"; }
    if (props.variant === "warning") { bg = "rgba(245,127,23,0.3)"; border = "rgba(245,127,23,0.5)"; }
    if (props.variant === "danger") { bg = "rgba(198,40,40,0.3)"; border = "rgba(198,40,40,0.5)"; }
    if (props.variant === "info") { bg = "rgba(2,119,189,0.3)"; border = "rgba(2,119,189,0.5)"; }
    return {
        padding: "6px 14px",
        borderRadius: "20px",
        fontSize: "0.78rem",
        fontWeight: 600,
        background: bg,
        border: "1px solid " + border,
        color: "#fff",
        whiteSpace: "nowrap",
    };
});

/* ─── Tab Bar ─── */
export const GYTabBar = styled("div")({
    display: "flex",
    gap: "4px",
    background: "#fff",
    borderRadius: "4px",
    padding: "4px",
    marginBottom: "8px",
    border: "1px solid #dce4ec",
    flexShrink: 0,
});

export const GYTabBtn = styled("button")(function (props) {
    const activeBg = props.color || PRIMARY;
    return {
        flex: "1 1 auto",
        padding: "8px 16px",
        border: "none",
        borderRadius: "2px",
        cursor: "pointer",
        fontSize: "0.85rem",
        fontWeight: props.active ? 600 : 400,
        background: props.active ? activeBg : "transparent",
        color: props.active ? "#fff" : "#555",
        transition: "all 0.2s",
        whiteSpace: "nowrap",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "6px",
        ":hover": { background: props.active ? activeBg : "#f5f5f5" },
    };
});

export const GYTabBadge = styled("span")(function (props) {
    return {
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        minWidth: "20px",
        height: "20px",
        borderRadius: "10px",
        fontSize: "0.7rem",
        fontWeight: 700,
        padding: "0 6px",
        background: props.active ? "rgba(255,255,255,0.25)" : "#e0e0e0",
        color: props.active ? "#fff" : "#666",
    };
});

/* ─── Tab Content Scroll Area ─── */
export const GYTabContent = styled("div")({
    flex: 1,
    overflowY: "auto",
    paddingRight: "4px",
    display: "flex",
    flexDirection: "column",
});

/* ─── Section Card ─── */
export const GYSectionCard = styled("div")({
    background: "#fff",
    borderRadius: "4px",
    marginBottom: "12px",
    border: "1px solid #dce4ec",
    overflow: "hidden",
});

export const GYSectionHeader = styled("div")(function (props) {
    const color = props.color || PRIMARY;
    return {
        padding: "10px 14px",
        fontWeight: 600,
        fontSize: "0.9rem",
        color: "#333",
        background: "#f0f4f8",
        borderBottom: "1px solid #dce4ec",
        display: "flex",
        alignItems: "center",
        gap: "8px",
    };
});

export const GYSectionBody = styled("div")({
    padding: "12px 14px",
});

/* ─── Wizard / Section Navigator ─── */
export const GYWizardBar = styled("div")({
    display: "flex",
    gap: "2px",
    background: "#fff",
    borderRadius: "4px",
    padding: "2px",
    marginBottom: "12px",
    border: "1px solid #dce4ec",
    overflowX: "auto",
    "&::-webkit-scrollbar": { height: 0 },
});

export const GYWizardStep = styled("button")(function (props) {
    let bg = "transparent";
    let color = "#666";
    let borderBottom = "2px solid transparent";
    if (props.active) {
        bg = PRIMARY_BG;
        color = PRIMARY;
        borderBottom = "2px solid " + PRIMARY;
    } else if (props.completed) {
        color = SUCCESS;
    }
    return {
        flex: "1 1 auto",
        padding: "8px 12px",
        border: "none",
        borderBottom: borderBottom,
        borderRadius: "2px",
        cursor: "pointer",
        fontSize: "0.85rem",
        fontWeight: props.active ? 600 : 400,
        background: bg,
        color: color,
        transition: "all 0.2s",
        whiteSpace: "nowrap",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "6px",
        ":hover": { background: props.active ? PRIMARY_BG : "#f9f9f9" },
    };
});

export const GYStepNumber = styled("span")(function (props) {
    return {
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: "22px",
        height: "22px",
        borderRadius: "50%",
        fontSize: "0.7rem",
        fontWeight: 700,
        background: props.active ? PRIMARY : (props.completed ? SUCCESS : "#e0e0e0"),
        color: (props.active || props.completed) ? "#fff" : "#999",
    };
});

/* ─── Stepper ─── */
export const GYStepper = styled("div")({
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "center",
    padding: "24px 40px 16px",
    background: "#fff",
    marginBottom: "16px",
    border: "1px solid #dce4ec",
    borderRadius: "4px",
    flexShrink: 0,
});

export const GYStepperItem = styled("button")({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "8px",
    border: "none",
    background: "transparent",
    cursor: "pointer",
    padding: "0",
    flexShrink: 0,
    minWidth: "100px",
});

export const GYStepperCircle = styled("div")(function (props) {
    return {
        width: "40px",
        height: "40px",
        borderRadius: "50%",
        background: props.active ? PRIMARY : (props.completed ? SUCCESS : "#e0e0e0"),
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontWeight: 700,
        fontSize: "0.9rem",
        color: (props.active || props.completed) ? "#fff" : "#888",
        transition: "all 0.25s",
        boxShadow: props.active ? "0 2px 10px " + PRIMARY + "50" : "none",
        flexShrink: 0,
    };
});

export const GYStepperConnector = styled("div")(function (props) {
    return {
        flex: 1,
        height: "2px",
        background: props.completed ? SUCCESS : "#e0e0e0",
        marginTop: "19px",
        transition: "background 0.25s",
        minWidth: "32px",
    };
});

export const GYStepperLabel = styled("span")(function (props) {
    return {
        fontSize: "0.78rem",
        fontWeight: props.active ? 700 : 500,
        color: props.active ? PRIMARY : (props.completed ? SUCCESS : "#aaa"),
        whiteSpace: "nowrap",
        textAlign: "center",
        transition: "color 0.25s",
    };
});

/* ─── Form Elements ─── */
export const GYFormRow = styled("div")(function (props) {
    return {
        display: "grid",
        gridTemplateColumns: props.cols || "1fr 1fr",
        gap: "14px",
        marginBottom: "14px",
        "@media(max-width: 768px)": { gridTemplateColumns: "1fr" },
    };
});

export const GYFormGroup = styled("div")({
    display: "flex",
    flexDirection: "column",
    gap: "4px",
});

export const GYLabel = styled("label")({
    fontSize: "0.78rem",
    fontWeight: 600,
    color: "#444",
    display: "flex",
    alignItems: "center",
    gap: "4px",
});

export const GYInput = styled("input")({
    padding: "6px 10px",
    border: "1px solid #ccc",
    borderRadius: "4px",
    fontSize: "0.85rem",
    outline: "none",
    transition: "border-color 0.2s",
    ":focus": { borderColor: PRIMARY },
    "::placeholder": { color: "#bbb" },
});

export const GYSelect = styled("select")({
    padding: "6px 10px",
    border: "1px solid #ccc",
    borderRadius: "4px",
    fontSize: "0.85rem",
    outline: "none",
    background: "#fff",
    cursor: "pointer",
    ":focus": { borderColor: PRIMARY },
});

export const GYTextarea = styled("textarea")({
    padding: "6px 10px",
    border: "1px solid #ccc",
    borderRadius: "4px",
    fontSize: "0.85rem",
    outline: "none",
    minHeight: "60px",
    resize: "vertical",
    fontFamily: "inherit",
    ":focus": { borderColor: PRIMARY },
    "::placeholder": { color: "#bbb" },
});

/* ─── Checkbox / Toggle Grid ─── */
export const GYCheckGrid = styled("div")(function (props) {
    return {
        display: "grid",
        gridTemplateColumns: props.cols || "1fr 1fr 1fr",
        gap: "8px",
        marginBottom: "12px",
        "@media(max-width: 768px)": { gridTemplateColumns: "1fr 1fr" },
    };
});

export const GYCheckItem = styled("label")(function (props) {
    return {
        display: "flex",
        alignItems: "center",
        gap: "6px",
        padding: "6px 10px",
        borderRadius: "4px",
        border: props.checked
            ? "1px solid " + (props.color || PRIMARY)
            : "1px solid #ccc",
        background: props.checked
            ? (props.color || PRIMARY) + "1a"
            : "#f8f8f8",
        cursor: "pointer",
        fontSize: "0.85rem",
        fontWeight: props.checked ? 600 : 400,
        color: props.checked ? (props.color || PRIMARY) : "#333",
        transition: "all 0.2s",
        ":hover": { borderColor: props.color || PRIMARY, background: (props.color || PRIMARY) + "0a" },
    };
});

export const GYCheckBox = styled("input")({
    accentColor: PRIMARY,
    width: "16px",
    height: "16px",
    cursor: "pointer",
});

/* ─── Inline Checkbox Row ─── */
export const GYCheckRow = styled("label")(function (props) {
    return {
        display: "flex",
        alignItems: "center",
        gap: "8px",
        cursor: "pointer",
        fontSize: "0.82rem",
        fontWeight: props.checked ? 600 : 400,
        color: props.checked ? PRIMARY : "#555",
        marginBottom: "6px",
    };
});

/* ─── Cycle Info Card ─── */
export const GYCycleCard = styled("div")({
    display: "flex",
    gap: "16px",
    flexWrap: "wrap",
    padding: "10px 14px",
    background: PRIMARY_BG,
    borderRadius: "4px",
    marginBottom: "12px",
    border: "1px solid " + PRIMARY + "30",
});

export const GYCycleStat = styled("div")({
    display: "flex",
    flexDirection: "column",
    gap: "2px",
});

export const GYCycleLabel = styled("span")({
    fontSize: "0.7rem",
    fontWeight: 600,
    color: PRIMARY,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
});

export const GYCycleValue = styled("span")(function (props) {
    return {
        fontSize: "0.95rem",
        fontWeight: 700,
        color: props.alert ? DANGER : SECONDARY,
    };
});

/* ─── GPAL Summary Pill ─── */
export const GYGPALPill = styled("div")({
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    padding: "6px 16px",
    borderRadius: "20px",
    background: PURPLE + "15",
    border: "1px solid " + PURPLE + "40",
    fontWeight: 700,
    fontSize: "0.9rem",
    color: PURPLE,
    marginBottom: "12px",
});

/* ─── Examination Section ─── */
export const GYExamSection = styled("div")({
    marginBottom: "16px",
});

export const GYExamHeader = styled("div")(function (props) {
    const color = props.color || PRIMARY;
    return {
        display: "flex",
        alignItems: "center",
        gap: "8px",
        padding: "10px 14px",
        background: color + "10",
        borderRadius: "8px 8px 0 0",
        borderLeft: "4px solid " + color,
        cursor: "pointer",
        fontWeight: 600,
        fontSize: "0.85rem",
        color: color,
        transition: "background 0.2s",
        ":hover": { background: color + "18" },
    };
});

export const GYExamBody = styled("div")(function (props) {
    return {
        padding: props.open ? "12px 14px" : "0 14px",
        maxHeight: props.open ? "2000px" : "0",
        overflow: "hidden",
        transition: "all 0.3s ease",
        borderLeft: "4px solid #e0e0e0",
    };
});

/* ─── Diagnosis Picker ─── */
export const GYDiagGrid = styled("div")({
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "6px",
    maxHeight: "300px",
    overflowY: "auto",
    marginBottom: "14px",
    "@media(max-width: 768px)": { gridTemplateColumns: "1fr" },
});

export const GYDiagItem = styled("button")(function (props) {
    return {
        display: "flex",
        alignItems: "center",
        gap: "8px",
        padding: "8px 12px",
        border: props.selected ? "2px solid " + INFO : "1px solid #e0e0e0",
        borderRadius: "6px",
        background: props.selected ? INFO + "0a" : "#fafafa",
        cursor: "pointer",
        textAlign: "left",
        fontSize: "0.78rem",
        color: props.selected ? INFO : "#444",
        fontWeight: props.selected ? 600 : 400,
        transition: "all 0.15s",
        ":hover": { borderColor: INFO, background: INFO + "08" },
    };
});

export const GYDiagCode = styled("span")({
    fontWeight: 700,
    fontSize: "0.72rem",
    color: "#fff",
    background: INFO,
    padding: "2px 6px",
    borderRadius: "4px",
    whiteSpace: "nowrap",
});

/* ─── Investigation Tags ─── */
export const GYInvGrid = styled("div")({
    display: "grid",
    gridTemplateColumns: "1fr 1fr 1fr",
    gap: "6px",
    marginBottom: "14px",
    "@media(max-width: 768px)": { gridTemplateColumns: "1fr 1fr" },
});

export const GYInvItem = styled("label")(function (props) {
    return {
        display: "flex",
        alignItems: "center",
        gap: "8px",
        padding: "7px 10px",
        borderRadius: "6px",
        border: props.checked ? "2px solid " + WARNING : "1px solid #e0e0e0",
        background: props.checked ? WARNING + "0a" : "#fafafa",
        cursor: "pointer",
        fontSize: "0.78rem",
        fontWeight: props.checked ? 600 : 400,
        color: props.checked ? WARNING : "#555",
        transition: "all 0.15s",
        ":hover": { borderColor: WARNING },
    };
});

/* ─── Alert Banners ─── */
export const GYAlertBanner = styled("div")(function (props) {
    const colors = {
        critical: { bg: "#fce4ec", border: "#ef9a9a", icon: DANGER },
        warning: { bg: "#fff8e1", border: "#ffe082", icon: WARNING },
        info: { bg: "#e3f2fd", border: "#90caf9", icon: INFO },
        success: { bg: "#e8f5e9", border: "#a5d6a7", icon: SUCCESS },
    };
    const c = colors[props.type] || colors.info;
    return {
        display: "flex",
        gap: "12px",
        padding: "12px 16px",
        borderRadius: "8px",
        background: c.bg,
        border: "1px solid " + c.border,
        marginBottom: "12px",
        alignItems: "flex-start",
    };
});

export const GYAlertIcon = styled("span")({ fontSize: "1.3rem", lineHeight: 1 });
export const GYAlertContent = styled("div")({ flex: 1, display: "flex", flexDirection: "column", gap: "2px" });
export const GYAlertTitle = styled("span")({ fontWeight: 700, fontSize: "0.82rem", color: "#333" });
export const GYAlertDetail = styled("span")({ fontSize: "0.78rem", color: "#555" });

/* ─── Action Bar ─── */
export const GYActionBar = styled("div")({
    display: "flex",
    gap: "10px",
    justifyContent: "flex-end",
    padding: "16px 0",
    flexWrap: "wrap",
});

export const GYPrimaryBtn = styled("button")(function (props) {
    return {
        padding: "8px 16px",
        borderRadius: "4px",
        border: "none",
        background: props.disabled ? "#ccc" : PRIMARY,
        color: "#fff",
        fontWeight: 600,
        fontSize: "0.85rem",
        cursor: props.disabled ? "not-allowed" : "pointer",
        transition: "background 0.2s",
        display: "flex",
        alignItems: "center",
        gap: "6px",
        ":hover": { background: props.disabled ? "#ccc" : PRIMARY_LIGHT },
    };
});

export const GYSecondaryBtn = styled("button")({
    padding: "8px 16px",
    borderRadius: "4px",
    border: "1px solid #ccc",
    background: "#fff",
    color: "#333",
    fontWeight: 500,
    fontSize: "0.85rem",
    cursor: "pointer",
    transition: "all 0.2s",
    display: "flex",
    alignItems: "center",
    gap: "6px",
    ":hover": { background: "#f0f4f8", borderColor: "#999" },
});

export const GYDangerBtn = styled("button")({
    padding: "8px 16px",
    borderRadius: "4px",
    border: "none",
    background: DANGER,
    color: "#fff",
    fontWeight: 600,
    fontSize: "0.85rem",
    cursor: "pointer",
    ":hover": { background: "#b71c1c" },
});

/* ─── Navigation Buttons ─── */
export const GYNavBar = styled("div")({
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "12px",
    borderTop: "1px solid #dce4ec",
    background: "#fff",
    position: "sticky",
    bottom: 0,
    zIndex: 100,
    marginTop: "auto",
    boxShadow: "0 -2px 10px rgba(0,0,0,0.05)",
});

/* ─── Timeline Styles ─── */
export const GYTimelineContainer = styled("div")({
    position: "relative",
    paddingLeft: "24px",
    "&::before": {
        content: '""',
        position: "absolute",
        left: "10px",
        top: "0",
        bottom: "0",
        width: "2px",
        background: PRIMARY + "40",
    },
});

export const GYTimelineItem = styled("div")({
    position: "relative",
    marginBottom: "20px",
    "&::before": {
        content: '""',
        position: "absolute",
        left: "-18px",
        top: "16px",
        width: "10px",
        height: "10px",
        borderRadius: "50%",
        background: PRIMARY,
        border: "2px solid " + PRIMARY_BG,
    },
});

export const GYTimelineCard = styled("div")({
    background: "#fff",
    border: "1px solid #e8e8e8",
    borderRadius: "10px",
    overflow: "hidden",
    transition: "box-shadow 0.2s",
    ":hover": { boxShadow: "0 2px 8px rgba(0,0,0,0.08)" },
});

export const GYTimelineHeader = styled("div")({
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "12px 16px",
    background: "#f9f9f9",
    borderBottom: "1px solid #eee",
    flexWrap: "wrap",
    gap: "8px",
});

export const GYTimelineDate = styled("span")({
    fontSize: "0.8rem",
    fontWeight: 600,
    color: PRIMARY,
});

export const GYTimelineDiag = styled("span")({
    fontSize: "0.82rem",
    fontWeight: 700,
    color: SECONDARY,
});

export const GYTimelineBody = styled("div")(function (props) {
    return {
        padding: props.open ? "12px 16px" : "0 16px",
        maxHeight: props.open ? "2000px" : "0",
        overflow: "hidden",
        transition: "all 0.3s ease",
    };
});

export const GYTimelineField = styled("div")({
    display: "flex",
    gap: "8px",
    marginBottom: "6px",
    fontSize: "0.8rem",
});

export const GYTimelineFieldLabel = styled("span")({
    fontWeight: 600,
    color: "#666",
    minWidth: "130px",
});

export const GYTimelineFieldValue = styled("span")({
    color: "#333",
    flex: 1,
});

export const GYTimelineActions = styled("div")({
    display: "flex",
    gap: "8px",
    padding: "10px 16px",
    borderTop: "1px solid #eee",
});

export const GYTimelineBtn = styled("button")(function (props) {
    const color = props.color || PRIMARY;
    return {
        padding: "6px 14px",
        borderRadius: "6px",
        border: "1px solid " + color + "40",
        background: color + "0a",
        color: color,
        cursor: "pointer",
        fontSize: "0.76rem",
        fontWeight: 600,
        transition: "all 0.15s",
        ":hover": { background: color + "18" },
    };
});

/* ─── Empty State ─── */
export const GYEmptyState = styled("div")({
    textAlign: "center",
    padding: "40px 20px",
});

export const GYEmptyIcon = styled("div")({
    fontSize: "3rem",
    marginBottom: "12px",
});

export const GYEmptyTitle = styled("h3")({
    margin: "0 0 6px",
    fontSize: "1.1rem",
    fontWeight: 600,
    color: "#555",
});

export const GYEmptyDesc = styled("p")({
    margin: 0,
    fontSize: "0.85rem",
    color: "#888",
});

/* ─── Status Badge ─── */
export const GYStatusBadge = styled("span")(function (props) {
    const color = props.color || PRIMARY;
    return {
        display: "inline-flex",
        alignItems: "center",
        gap: "4px",
        padding: "3px 10px",
        borderRadius: "12px",
        fontSize: "0.72rem",
        fontWeight: 600,
        background: color + "15",
        color: color,
        border: "1px solid " + color + "30",
    };
});

/* ─── Loading ─── */
export const GYLoading = styled("div")({
    textAlign: "center",
    padding: "60px 20px",
    fontSize: "0.95rem",
    color: "#888",
});

/* ─── Search / Filter ─── */
export const GYSearchInput = styled("input")({
    padding: "8px 14px",
    border: "1px solid #ddd",
    borderRadius: "8px",
    fontSize: "0.84rem",
    outline: "none",
    width: "100%",
    maxWidth: "400px",
    ":focus": { borderColor: PRIMARY },
    "::placeholder": { color: "#bbb" },
});

/* ─── Tag Pill (for investigations, diagnoses etc.) ─── */
export const GYTagPill = styled("span")(function (props) {
    const color = props.color || PRIMARY;
    return {
        display: "inline-flex",
        alignItems: "center",
        gap: "4px",
        padding: "3px 10px",
        borderRadius: "14px",
        fontSize: "0.72rem",
        fontWeight: 600,
        background: color + "12",
        color: color,
        border: "1px solid " + color + "30",
        marginRight: "4px",
        marginBottom: "4px",
    };
});

/* ─── Required Field Star ─── */
export const GYRequiredStar = styled("span")({
    color: "#d32f2f",
    fontWeight: 700,
    marginLeft: "2px",
    fontSize: "0.85rem",
});

/* ─── Field Error Message ─── */
export const GYFieldError = styled("span")({
    color: "#d32f2f",
    fontSize: "0.72rem",
    fontWeight: 500,
    marginTop: "2px",
    display: "flex",
    alignItems: "center",
    gap: "4px",
});

/* ─── Referral Card ─── */
export const GYReferralCard = styled("div")(function (props) {
    return {
        padding: "12px 16px",
        background: props.needed ? WARNING + "08" : "#f9f9f9",
        border: "1px solid " + (props.needed ? WARNING + "40" : "#e0e0e0"),
        borderRadius: "8px",
        marginTop: "12px",
    };
});
